import { useState, useMemo } from "react";

export type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectProps = {
  options: MultiSelectOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  ariaLabel?: string;
};

export default function MultiSelect({
  options,
  value,
  onChange,
  disabled = false,
  searchable = true,
  searchPlaceholder = "Search...",
  emptyText = "No options found",
  ariaLabel = "Options",
}: MultiSelectProps) {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter((x) =>
      x.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const valueSet = new Set(value);

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((x) => valueSet.has(x.value));
  const someSelected = filteredOptions.some((x) => valueSet.has(x.value));

  const handleSelectAll = () => {
    if (allSelected) {
      const filteredValues = new Set(filteredOptions.map((x) => x.value));
      onChange(value.filter((x) => !filteredValues.has(x)));
    } else {
      const newSelections = filteredOptions
        .map((x) => x.value)
        .filter((v) => !valueSet.has(v));
      onChange([...value, ...newSelections]);
    }
  };

  const handleToggle = (val: string) => {
    if (valueSet.has(val)) {
      onChange(value.filter((x) => x !== val));
    } else {
      onChange([...value, val]);
    }
  };

  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 6,
        overflow: "hidden",
        background: disabled ? "#f8f9fa" : "#fff",
      }}
    >
      {searchable && (
        <div style={{ padding: "8px", borderBottom: "1px solid #e2e8f0" }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}

      <div
        style={{
          padding: "6px 8px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <label
          className="d-flex align-items-center gap-2 mb-0"
          style={{
            cursor: disabled ? "default" : "pointer",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={handleSelectAll}
            disabled={disabled}
            style={{
              width: "1rem",
              height: "1rem",
              accentColor: "#0d2d4a",
              cursor: disabled ? "default" : "pointer",
            }}
          />
          Select All
          {value.length > 0 && (
            <span
              style={{ fontSize: "0.8rem", color: "#374151", fontWeight: 500 }}
            >
              ({value.length} selected)
            </span>
          )}
        </label>
      </div>

      <div
        role="listbox"
        aria-multiselectable="true"
        aria-label={ariaLabel}
        style={{ maxHeight: 200, overflowY: "auto" }}
      >
        {filteredOptions.length === 0 ? (
          <div
            className="text-muted text-center py-3"
            style={{ fontSize: "0.875rem" }}
          >
            {emptyText}
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = valueSet.has(option.value);
            return (
              <label
                key={option.value}
                role="option"
                aria-selected={isSelected}
                className="d-flex align-items-center gap-2 mb-0 px-2 py-1"
                style={{
                  cursor: disabled ? "default" : "pointer",
                  fontSize: "0.875rem",
                  color: isSelected ? "#0d2d4a" : "#1e293b",
                  background: isSelected
                    ? "rgba(13, 45, 74, 0.05)"
                    : "transparent",
                  transition: "background 0.1s",
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(option.value)}
                  disabled={disabled}
                  style={{
                    width: "1rem",
                    height: "1rem",
                    accentColor: "#0d2d4a",
                    cursor: disabled ? "default" : "pointer",
                    flexShrink: 0,
                  }}
                />
                {option.label}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
